import React, { useEffect, useState } from 'react';
import { Smartphone, Apple, Monitor, Download, Share2, Plus, Check, AlertTriangle, ChevronRight, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLang } from '@/context/LangContext';

const LOGO = '/logo.png';

// Self-contained install-page i18n (kept here to avoid bloating the main dict)
const I = {
  es: {
    title: 'Instala la app',
    subtitle: 'Acceso con un toque, pantalla completa sin barra del navegador, modo offline y notificaciones de partidos en directo.',
    already_installed: '¡App ya instalada!',
    quick_install_avail: 'Instalación rápida disponible',
    quick_install_supports: 'Tu navegador soporta la instalación con un solo toque:',
    install_btn: 'Instalar PlayMaster IA',
    installed_ok: '✓ Instalada con éxito',
    cancelled: 'Instalación cancelada',
    dialog_error: 'No se pudo abrir el diálogo automático. Usa las instrucciones manuales abajo.',
    choose_device: 'Elige tu dispositivo:',
    tab_ios: 'iPhone / iPad', tab_android: 'Android', tab_desktop: 'PC / Mac',
    ios_h: 'En tu iPhone o iPad',
    ios_warn: 'Apple sólo permite instalar PWAs desde Safari. Si estás en Chrome o Firefox, copia esta URL y ábrela en Safari primero.',
    ios_s1: 'Abre la app en Safari y toca el botón Compartir ⤴ (abajo en iPhone, arriba en iPad).',
    ios_s2: 'En el menú, desliza hacia abajo y toca "Añadir a pantalla de inicio".',
    ios_s3: 'Confirma con "Añadir" (arriba a la derecha) — el icono dorado aparecerá en tu home.',
    ios_s4: 'Toca el icono desde la pantalla de inicio para abrir la app en modo completo.',
    and_h: 'En tu Android',
    and_reco: 'Recomendado: Chrome o Edge. Samsung Internet y Firefox también funcionan.',
    and_s1: 'Abre PlayMaster IA en Chrome.',
    and_s2: 'Si aparece el banner "Añadir a pantalla de inicio" en la parte inferior, tócalo.',
    and_s3: 'Si no aparece, abre el menú del navegador (⋮ arriba a la derecha) y elige "Instalar aplicación" o "Añadir a pantalla de inicio".',
    and_s4: 'Confirma con "Instalar". El icono aparecerá en tu cajón de apps y en la pantalla de inicio.',
    and_tip: '💡 Si el banner no aparece, prueba a recargar la página o navega 30 segundos por la app — Chrome muestra la opción cuando detecta interacción real.',
    and_play_protect_title: '⚠️ Sobre la advertencia de Google Play Protect',
    and_play_protect_body: 'Si al instalar ves "Se bloqueó la app no segura" o "Esta app se diseñó para una versión anterior de Android", NO te preocupes — pulsa "Instalar de todas formas" y luego "Entendido". El aviso aparece porque PlayMaster IA es una PWA (no se distribuye por Play Store). Asegúrate de instalar desde el menú ⋮ de Chrome ("Instalar aplicación") y no desde un archivo APK externo — Chrome genera un WebAPK actualizado automáticamente.',
    pc_h: 'En tu PC o Mac',
    pc_reco: 'Recomendado: Chrome, Edge u Opera.',
    pc_section: 'Chrome / Edge / Opera (Windows, Mac, Linux)',
    pc_s1: 'Abre PlayMaster IA en el navegador.',
    pc_s2: 'Mira la barra de direcciones a la derecha: aparece un icono de instalación ⊕ — haz click.',
    pc_s3: 'O abre el menú ⋮ (arriba a la derecha) → "Instalar PlayMaster IA..." o "Cast, save and share" → "Install page as app".',
    pc_s4: 'Confirma con "Instalar". La app se abrirá en su propia ventana sin barra del navegador.',
    firefox_warn: 'Estás usando Firefox. En desktop, Firefox no soporta instalación de PWAs. Usa Chrome, Edge u Opera para instalar.',
    safari_warn: 'En Safari para Mac ve a File → Add to Dock... (macOS Sonoma 14+). En versiones anteriores no se puede instalar en desktop — usa Chrome/Edge.',
    faq_title: 'Preguntas frecuentes',
    faq1_q: '¿Es gratis?', faq1_a: 'Sí, instalar la app es 100% gratuito. Las suscripciones premium (Coach $9.99, Player $14.99, Academia $49.99) son opcionales y desbloquean funcionalidades avanzadas.',
    faq2_q: '¿Funciona sin conexión?', faq2_a: 'Sí. Una vez instalada, la app cachea las jugadas que has abierto y puedes consultarlas sin Internet. Para crear o editar jugadas y usar el IA Coach necesitas conexión.',
    faq3_q: '¿Ocupa mucho espacio?', faq3_a: 'Menos de 10 MB. PlayMaster IA es una PWA — usa caché del navegador, no requiere descargar la app entera como las apps nativas.',
    faq4_q: '¿Cómo desinstalo?', faq4_a: "iOS/iPadOS: mantén pulsado el icono → 'Eliminar app'. Android: arrastra el icono al icono de 'Desinstalar'. PC/Mac: abre la app → menú ⋮ → 'Desinstalar'.",
    faq5_q: '¿Por qué no me aparece el botón de instalación?', faq5_a: 'Tres causas habituales: (1) Estás en Firefox o en otro navegador no soportado — usa Chrome o Edge. (2) Ya tienes la app instalada (busca el icono en tu home). (3) Tu navegador necesita más interacción — navega 30 segundos por la app y refresca.',
    faq6_q: '¿Las notificaciones funcionan después de instalar?', faq6_a: 'Sí, en Android y PC/Mac. En iOS/iPadOS, las notificaciones push para PWAs requieren iOS 16.4+ y deben activarse manualmente desde Ajustes → Notificaciones → PlayMaster IA.',
    back_home: 'Volver al inicio',
  },
  en: {
    title: 'Install the app',
    subtitle: 'One-tap access, full screen with no browser bar, offline mode and live-game notifications.',
    already_installed: 'App already installed!',
    quick_install_avail: 'Quick install available',
    quick_install_supports: 'Your browser supports one-tap install:',
    install_btn: 'Install PlayMaster IA',
    installed_ok: '✓ Installed successfully',
    cancelled: 'Install cancelled',
    dialog_error: 'Could not open the automatic dialog. Use the manual instructions below.',
    choose_device: 'Choose your device:',
    tab_ios: 'iPhone / iPad', tab_android: 'Android', tab_desktop: 'PC / Mac',
    ios_h: 'On your iPhone or iPad',
    ios_warn: 'Apple only allows installing PWAs from Safari. If you are in Chrome or Firefox, copy this URL and open it in Safari first.',
    ios_s1: 'Open the app in Safari and tap the Share button ⤴ (bottom on iPhone, top on iPad).',
    ios_s2: 'In the menu, scroll down and tap "Add to Home Screen".',
    ios_s3: 'Confirm with "Add" (top right) — the gold icon will appear on your home screen.',
    ios_s4: 'Tap the icon from your home screen to open the app full-screen.',
    and_h: 'On your Android',
    and_reco: 'Recommended: Chrome or Edge. Samsung Internet and Firefox also work.',
    and_s1: 'Open PlayMaster IA in Chrome.',
    and_s2: 'If the "Add to Home Screen" banner appears at the bottom, tap it.',
    and_s3: 'If it doesn\'t appear, open the browser menu (⋮ top right) and choose "Install app" or "Add to Home Screen".',
    and_s4: 'Confirm with "Install". The icon will appear in your app drawer and home screen.',
    and_tip: '💡 If the banner doesn\'t appear, try reloading the page or browse for 30 seconds — Chrome shows the option when it detects real interaction.',
    and_play_protect_title: '⚠️ About the Google Play Protect warning',
    and_play_protect_body: 'If you see "Blocked unsafe app" or "This app was designed for an older Android version" when installing, do NOT worry — tap "Install anyway" and then "Got it". The warning appears because PlayMaster IA is a PWA (not distributed via Play Store). Be sure to install from Chrome\'s ⋮ menu ("Install app") and NOT from an external APK file — Chrome generates an up-to-date WebAPK automatically.',
    pc_h: 'On your PC or Mac',
    pc_reco: 'Recommended: Chrome, Edge or Opera.',
    pc_section: 'Chrome / Edge / Opera (Windows, Mac, Linux)',
    pc_s1: 'Open PlayMaster IA in the browser.',
    pc_s2: 'Look at the address bar on the right: an install icon ⊕ appears — click it.',
    pc_s3: 'Or open the ⋮ menu (top right) → "Install PlayMaster IA..." or "Cast, save and share" → "Install page as app".',
    pc_s4: 'Confirm with "Install". The app will open in its own window with no browser bar.',
    firefox_warn: "You're using Firefox. On desktop, Firefox doesn't support PWA install. Use Chrome, Edge or Opera.",
    safari_warn: 'In Safari for Mac go to File → Add to Dock... (macOS Sonoma 14+). In older versions you can\'t install on desktop — use Chrome/Edge.',
    faq_title: 'Frequently asked questions',
    faq1_q: 'Is it free?', faq1_a: 'Yes, installing the app is 100% free. Premium subscriptions (Coach $9.99, Player $14.99, Academy $49.99) are optional and unlock advanced features.',
    faq2_q: 'Does it work offline?', faq2_a: 'Yes. Once installed, the app caches plays you\'ve opened and you can browse them without Internet. Creating or editing plays and using the AI Coach require a connection.',
    faq3_q: 'Does it take up much space?', faq3_a: 'Less than 10 MB. PlayMaster IA is a PWA — it uses browser cache, no full app download required.',
    faq4_q: 'How do I uninstall?', faq4_a: "iOS/iPadOS: long-press the icon → 'Remove app'. Android: drag the icon to 'Uninstall'. PC/Mac: open the app → ⋮ menu → 'Uninstall'.",
    faq5_q: "Why don't I see the install button?", faq5_a: "Three common causes: (1) You're in Firefox or another unsupported browser — use Chrome or Edge. (2) You already have the app installed (check your home screen). (3) Your browser needs more interaction — browse for 30 seconds and refresh.",
    faq6_q: 'Do notifications work after installing?', faq6_a: 'Yes, on Android and PC/Mac. On iOS/iPadOS, push notifications for PWAs require iOS 16.4+ and must be enabled manually from Settings → Notifications → PlayMaster IA.',
    back_home: 'Back to home',
  },
  fr: {
    title: "Installer l'app",
    subtitle: 'Accès en un toucher, plein écran sans barre de navigateur, mode hors-ligne et notifications de matchs en direct.',
    already_installed: "L'app est déjà installée !",
    quick_install_avail: 'Installation rapide disponible',
    quick_install_supports: 'Votre navigateur permet une installation en un seul clic :',
    install_btn: 'Installer PlayMaster IA',
    installed_ok: '✓ Installation réussie',
    cancelled: 'Installation annulée',
    dialog_error: "Impossible d'ouvrir le dialogue automatique. Utilisez les instructions manuelles ci-dessous.",
    choose_device: 'Choisissez votre appareil :',
    tab_ios: 'iPhone / iPad', tab_android: 'Android', tab_desktop: 'PC / Mac',
    ios_h: 'Sur votre iPhone ou iPad',
    ios_warn: "Apple n'autorise l'installation de PWAs que depuis Safari. Si vous êtes sur Chrome ou Firefox, copiez cette URL et ouvrez-la d'abord dans Safari.",
    ios_s1: "Ouvrez l'app dans Safari et touchez le bouton Partager ⤴ (en bas sur iPhone, en haut sur iPad).",
    ios_s2: 'Dans le menu, faites défiler vers le bas et touchez « Sur l\'écran d\'accueil ».',
    ios_s3: 'Confirmez avec « Ajouter » (en haut à droite) — l\'icône dorée apparaîtra sur votre écran d\'accueil.',
    ios_s4: "Touchez l'icône depuis l'écran d'accueil pour ouvrir l'app en plein écran.",
    and_h: 'Sur votre Android',
    and_reco: 'Recommandé : Chrome ou Edge. Samsung Internet et Firefox fonctionnent aussi.',
    and_s1: 'Ouvrez PlayMaster IA dans Chrome.',
    and_s2: 'Si la bannière « Ajouter à l\'écran d\'accueil » apparaît en bas, touchez-la.',
    and_s3: 'Sinon, ouvrez le menu du navigateur (⋮ en haut à droite) et choisissez « Installer l\'application » ou « Ajouter à l\'écran d\'accueil ».',
    and_s4: 'Confirmez avec « Installer ». L\'icône apparaîtra dans votre tiroir d\'apps et sur l\'écran d\'accueil.',
    and_tip: '💡 Si la bannière n\'apparaît pas, rechargez la page ou naviguez 30 secondes — Chrome montre l\'option quand il détecte une interaction réelle.',
    pc_h: 'Sur votre PC ou Mac',
    pc_reco: 'Recommandé : Chrome, Edge ou Opera.',
    pc_section: 'Chrome / Edge / Opera (Windows, Mac, Linux)',
    pc_s1: 'Ouvrez PlayMaster IA dans le navigateur.',
    pc_s2: "Regardez la barre d'adresse à droite : une icône d'installation ⊕ apparaît — cliquez dessus.",
    pc_s3: 'Ou ouvrez le menu ⋮ (en haut à droite) → « Installer PlayMaster IA... » ou « Caster, enregistrer et partager » → « Install page as app ».',
    pc_s4: 'Confirmez avec « Installer ». L\'app s\'ouvrira dans sa propre fenêtre sans barre du navigateur.',
    firefox_warn: "Vous utilisez Firefox. Sur desktop, Firefox ne supporte pas l'installation de PWAs. Utilisez Chrome, Edge ou Opera.",
    safari_warn: 'Dans Safari pour Mac, allez dans File → Add to Dock... (macOS Sonoma 14+). Dans les versions antérieures, l\'installation desktop n\'est pas possible — utilisez Chrome/Edge.',
    faq_title: 'Questions fréquentes',
    faq1_q: 'Est-ce gratuit ?', faq1_a: "Oui, installer l'app est 100% gratuit. Les abonnements premium (Coach 9,99 $, Player 14,99 $, Académie 49,99 $) sont facultatifs et débloquent des fonctionnalités avancées.",
    faq2_q: 'Fonctionne-t-elle hors-ligne ?', faq2_a: "Oui. Une fois installée, l'app met en cache les jeux ouverts et vous pouvez les consulter sans Internet. Créer/éditer des jeux et utiliser l'IA Coach nécessitent une connexion.",
    faq3_q: 'Prend-elle beaucoup de place ?', faq3_a: "Moins de 10 Mo. PlayMaster IA est une PWA — elle utilise le cache du navigateur, pas besoin de téléchargement complet comme les apps natives.",
    faq4_q: 'Comment désinstaller ?', faq4_a: 'iOS/iPadOS : maintenez l\'icône → « Supprimer ». Android : glissez l\'icône vers « Désinstaller ». PC/Mac : ouvrez l\'app → menu ⋮ → « Désinstaller ».',
    faq5_q: "Pourquoi je ne vois pas le bouton d'installation ?", faq5_a: "Trois causes habituelles : (1) Vous êtes sur Firefox ou un autre navigateur non supporté — utilisez Chrome ou Edge. (2) L'app est déjà installée (cherchez l'icône sur l'accueil). (3) Le navigateur a besoin de plus d'interaction — naviguez 30 secondes et rafraîchissez.",
    faq6_q: "Les notifications fonctionnent-elles après installation ?", faq6_a: "Oui, sur Android et PC/Mac. Sur iOS/iPadOS, les notifications push pour PWAs nécessitent iOS 16.4+ et doivent être activées manuellement depuis Réglages → Notifications → PlayMaster IA.",
    back_home: "Retour à l'accueil",
  },
  pt: {
    title: 'Instalar o app',
    subtitle: 'Acesso com um toque, tela cheia sem barra do navegador, modo offline e notificações de jogos ao vivo.',
    already_installed: 'O app já está instalado!',
    quick_install_avail: 'Instalação rápida disponível',
    quick_install_supports: 'Seu navegador suporta instalação com um clique:',
    install_btn: 'Instalar PlayMaster IA',
    installed_ok: '✓ Instalado com sucesso',
    cancelled: 'Instalação cancelada',
    dialog_error: 'Não foi possível abrir o diálogo automático. Use as instruções manuais abaixo.',
    choose_device: 'Escolha seu dispositivo:',
    tab_ios: 'iPhone / iPad', tab_android: 'Android', tab_desktop: 'PC / Mac',
    ios_h: 'No seu iPhone ou iPad',
    ios_warn: 'A Apple só permite instalar PWAs pelo Safari. Se estiver no Chrome ou Firefox, copie a URL e abra no Safari primeiro.',
    ios_s1: 'Abra o app no Safari e toque o botão Compartilhar ⤴ (embaixo no iPhone, acima no iPad).',
    ios_s2: 'No menu, role para baixo e toque "Adicionar à Tela de Início".',
    ios_s3: 'Confirme com "Adicionar" (canto superior direito) — o ícone dourado aparecerá na tela inicial.',
    ios_s4: 'Toque no ícone para abrir o app em tela cheia.',
    and_h: 'No seu Android',
    and_reco: 'Recomendado: Chrome ou Edge. Samsung Internet e Firefox também funcionam.',
    and_s1: 'Abra PlayMaster IA no Chrome.',
    and_s2: 'Se aparecer o banner "Adicionar à Tela Inicial" embaixo, toque nele.',
    and_s3: 'Se não aparecer, abra o menu do navegador (⋮ canto superior direito) e escolha "Instalar app" ou "Adicionar à Tela Inicial".',
    and_s4: 'Confirme com "Instalar". O ícone aparecerá no gaveta de apps e na tela inicial.',
    and_tip: '💡 Se o banner não aparecer, recarregue ou navegue 30 segundos — o Chrome mostra a opção quando detecta interação real.',
    and_play_protect_title: '⚠️ Sobre o aviso do Google Play Protect',
    and_play_protect_body: 'Se ao instalar você vir "App não seguro bloqueado" ou "Este app foi desenvolvido para uma versão mais antiga do Android", NÃO se preocupe — toque em "Instalar mesmo assim" e depois em "Entendi". O aviso aparece porque o PlayMaster IA é um PWA (não distribuído pela Play Store). Instale pelo menu ⋮ do Chrome ("Instalar app") e NÃO por um arquivo APK externo — o Chrome gera um WebAPK atualizado automaticamente.',
    pc_h: 'No seu PC ou Mac',
    pc_reco: 'Recomendado: Chrome, Edge ou Opera.',
    pc_section: 'Chrome / Edge / Opera (Windows, Mac, Linux)',
    pc_s1: 'Abra PlayMaster IA no navegador.',
    pc_s2: 'Olhe a barra de endereço à direita: aparece um ícone de instalação ⊕ — clique nele.',
    pc_s3: 'Ou abra o menu ⋮ (canto superior direito) → "Instalar PlayMaster IA..." ou "Cast, salvar e compartilhar" → "Install page as app".',
    pc_s4: 'Confirme com "Instalar". O app abrirá em sua própria janela sem barra do navegador.',
    firefox_warn: 'Você está usando Firefox. No desktop, o Firefox não suporta instalação de PWAs. Use Chrome, Edge ou Opera.',
    safari_warn: 'No Safari para Mac vá em File → Add to Dock... (macOS Sonoma 14+). Em versões anteriores não é possível instalar no desktop — use Chrome/Edge.',
    faq_title: 'Perguntas frequentes',
    faq1_q: 'É grátis?', faq1_a: 'Sim, instalar o app é 100% grátis. Assinaturas premium (Coach US$ 9,99; Player US$ 14,99; Academia US$ 49,99) são opcionais e desbloqueiam recursos avançados.',
    faq2_q: 'Funciona offline?', faq2_a: 'Sim. Depois de instalado, o app armazena as jogadas abertas e você pode consultá-las sem Internet. Criar/editar jogadas e usar o IA Coach exigem conexão.',
    faq3_q: 'Ocupa muito espaço?', faq3_a: 'Menos de 10 MB. PlayMaster IA é uma PWA — usa cache do navegador, sem necessidade de baixar o app inteiro.',
    faq4_q: 'Como desinstalo?', faq4_a: "iOS/iPadOS: pressione o ícone → 'Remover app'. Android: arraste para 'Desinstalar'. PC/Mac: abra o app → menu ⋮ → 'Desinstalar'.",
    faq5_q: 'Por que não vejo o botão de instalação?', faq5_a: 'Três causas comuns: (1) Você está no Firefox ou outro navegador não suportado — use Chrome ou Edge. (2) O app já está instalado (procure na tela inicial). (3) O navegador precisa de mais interação — navegue 30 segundos e atualize.',
    faq6_q: 'As notificações funcionam após instalar?', faq6_a: 'Sim, no Android e PC/Mac. No iOS/iPadOS, notificações push para PWAs exigem iOS 16.4+ e devem ser ativadas em Ajustes → Notificações → PlayMaster IA.',
    back_home: 'Voltar ao início',
  },
  zh: {
    title: '安装应用',
    subtitle: '一键访问、无浏览器栏全屏、离线模式以及实时比赛通知。',
    already_installed: '应用已安装！',
    quick_install_avail: '可快速安装',
    quick_install_supports: '您的浏览器支持一键安装：',
    install_btn: '安装 PlayMaster IA',
    installed_ok: '✓ 安装成功',
    cancelled: '安装已取消',
    dialog_error: '无法打开自动对话框。请使用下面的手动说明。',
    choose_device: '选择您的设备：',
    tab_ios: 'iPhone / iPad', tab_android: 'Android', tab_desktop: 'PC / Mac',
    ios_h: '在您的 iPhone 或 iPad 上',
    ios_warn: 'Apple 仅允许从 Safari 安装 PWA。如果您在 Chrome 或 Firefox 中，请先复制此 URL 并在 Safari 中打开。',
    ios_s1: '在 Safari 中打开应用并点击分享 ⤴（iPhone 在底部，iPad 在顶部）。',
    ios_s2: '在菜单中向下滚动并点击"添加到主屏幕"。',
    ios_s3: '在右上角确认"添加"——金色图标将出现在主屏幕上。',
    ios_s4: '从主屏幕点击图标以全屏方式打开应用。',
    and_h: '在您的 Android 上',
    and_reco: '推荐：Chrome 或 Edge。Samsung Internet 和 Firefox 也可以。',
    and_s1: '在 Chrome 中打开 PlayMaster IA。',
    and_s2: '如果底部出现"添加到主屏幕"横幅，请点击。',
    and_s3: '如果未显示，请打开浏览器菜单（⋮ 右上角）并选择"安装应用"或"添加到主屏幕"。',
    and_s4: '确认"安装"。图标将出现在应用抽屉和主屏幕中。',
    and_tip: '💡 如果横幅未出现，请重新加载页面或浏览 30 秒——Chrome 在检测到真实互动时才会显示选项。',
    and_play_protect_title: '⚠️ 关于 Google Play Protect 警告',
    and_play_protect_body: '如果安装时看到"已阻止不安全应用"或"此应用是为较早 Android 版本设计的"，无需担心——点击"仍要安装"然后"知道了"。出现警告是因为 PlayMaster IA 是 PWA（不通过 Play Store 分发）。请通过 Chrome 的 ⋮ 菜单（"安装应用"）安装，而不是通过外部 APK 文件——Chrome 会自动生成最新的 WebAPK。',
    pc_h: '在您的 PC 或 Mac 上',
    pc_reco: '推荐：Chrome、Edge 或 Opera。',
    pc_section: 'Chrome / Edge / Opera（Windows、Mac、Linux）',
    pc_s1: '在浏览器中打开 PlayMaster IA。',
    pc_s2: '查看右侧地址栏：出现安装图标 ⊕——点击它。',
    pc_s3: '或打开 ⋮ 菜单（右上角）→"安装 PlayMaster IA..."或"投射、保存和分享"→"将页面安装为应用"。',
    pc_s4: '确认"安装"。应用将在没有浏览器栏的独立窗口中打开。',
    firefox_warn: '您正在使用 Firefox。在桌面版中，Firefox 不支持安装 PWA。请使用 Chrome、Edge 或 Opera。',
    safari_warn: '在 Mac 的 Safari 中，前往"文件→添加到程序坞..."（macOS Sonoma 14+）。在较旧版本中无法在桌面安装——请使用 Chrome/Edge。',
    faq_title: '常见问题',
    faq1_q: '免费吗？', faq1_a: '是的，安装应用 100% 免费。高级订阅（教练 $9.99、球员 $14.99、学院 $49.99）是可选的，可解锁高级功能。',
    faq2_q: '可以离线使用吗？', faq2_a: '可以。安装后，应用会缓存您打开过的战术，可以离线查看。创建/编辑战术和使用 AI 教练需要联网。',
    faq3_q: '占用空间大吗？', faq3_a: '小于 10 MB。PlayMaster IA 是 PWA——使用浏览器缓存，不需要像原生应用那样完整下载。',
    faq4_q: '如何卸载？', faq4_a: 'iOS/iPadOS：长按图标→"移除应用"。Android：拖到"卸载"。PC/Mac：打开应用→⋮ 菜单→"卸载"。',
    faq5_q: '为什么没有安装按钮？', faq5_a: '三个常见原因：(1) 您在 Firefox 或其他不支持的浏览器中——请使用 Chrome 或 Edge。(2) 应用已安装（在主屏幕查找）。(3) 浏览器需要更多互动——浏览 30 秒后刷新。',
    faq6_q: '安装后通知能用吗？', faq6_a: '在 Android 和 PC/Mac 上可以。在 iOS/iPadOS 上，PWA 推送通知需要 iOS 16.4+，并需要在 设置→通知→PlayMaster IA 手动启用。',
    back_home: '返回首页',
  },
  ar: {
    title: 'ثبّت التطبيق',
    subtitle: 'وصول بنقرة، شاشة كاملة دون شريط متصفح، وضع غير متصل وإشعارات المباريات المباشرة.',
    already_installed: 'التطبيق مثبت بالفعل!',
    quick_install_avail: 'تثبيت سريع متاح',
    quick_install_supports: 'متصفحك يدعم التثبيت بنقرة واحدة:',
    install_btn: 'ثبّت PlayMaster IA',
    installed_ok: '✓ تم التثبيت بنجاح',
    cancelled: 'تم إلغاء التثبيت',
    dialog_error: 'تعذّر فتح المحاور التلقائي. استخدم التعليمات اليدوية أدناه.',
    choose_device: 'اختر جهازك:',
    tab_ios: 'iPhone / iPad', tab_android: 'Android', tab_desktop: 'PC / Mac',
    ios_h: 'على iPhone أو iPad',
    ios_warn: 'تسمح Apple بتثبيت PWAs من Safari فقط. إذا كنت في Chrome أو Firefox، انسخ الرابط وافتحه في Safari أولًا.',
    ios_s1: 'افتح التطبيق في Safari واضغط زر مشاركة ⤴ (أسفل iPhone، أعلى iPad).',
    ios_s2: 'في القائمة، مرّر للأسفل واضغط "إضافة إلى الشاشة الرئيسية".',
    ios_s3: 'أكّد بـ "إضافة" (أعلى اليمين) — ستظهر الأيقونة الذهبية على شاشتك الرئيسية.',
    ios_s4: 'اضغط الأيقونة من الشاشة الرئيسية لفتح التطبيق بملء الشاشة.',
    and_h: 'على Android',
    and_reco: 'موصى به: Chrome أو Edge. Samsung Internet و Firefox يعملان أيضًا.',
    and_s1: 'افتح PlayMaster IA في Chrome.',
    and_s2: 'إذا ظهر شعار "إضافة إلى الشاشة الرئيسية" أسفل الشاشة، اضغط عليه.',
    and_s3: 'إذا لم يظهر، افتح قائمة المتصفح (⋮ أعلى اليمين) واختر "تثبيت التطبيق" أو "إضافة إلى الشاشة الرئيسية".',
    and_s4: 'أكّد بـ "تثبيت". ستظهر الأيقونة في درج التطبيقات وعلى الشاشة الرئيسية.',
    and_tip: '💡 إذا لم يظهر الشعار، أعد تحميل الصفحة أو تصفّح 30 ثانية — يعرض Chrome الخيار عند اكتشاف تفاعل حقيقي.',
    and_play_protect_title: '⚠️ بشأن تحذير Google Play Protect',
    and_play_protect_body: 'إذا ظهرت رسالة "تم حظر التطبيق غير الآمن" أو "هذا التطبيق صُمم لإصدار Android أقدم" عند التثبيت، فلا داعي للقلق — اضغط "تثبيت على أي حال" ثم "حسنًا". يظهر التحذير لأن PlayMaster IA هو PWA (لا يُوزَّع عبر Play Store). ثبّت من قائمة ⋮ في Chrome ("تثبيت التطبيق") وليس من ملف APK خارجي — يولّد Chrome WebAPK محدّثًا تلقائيًا.',
    pc_h: 'على PC أو Mac',
    pc_reco: 'موصى به: Chrome أو Edge أو Opera.',
    pc_section: 'Chrome / Edge / Opera (Windows, Mac, Linux)',
    pc_s1: 'افتح PlayMaster IA في المتصفح.',
    pc_s2: 'انظر شريط العنوان على اليمين: تظهر أيقونة تثبيت ⊕ — انقر عليها.',
    pc_s3: 'أو افتح قائمة ⋮ (أعلى اليمين) → "تثبيت PlayMaster IA..." أو "Cast, save and share" → "Install page as app".',
    pc_s4: 'أكّد بـ "تثبيت". سيُفتح التطبيق في نافذته الخاصة دون شريط متصفح.',
    firefox_warn: 'تستخدم Firefox. في سطح المكتب، لا يدعم Firefox تثبيت PWAs. استخدم Chrome أو Edge أو Opera.',
    safari_warn: 'في Safari لـ Mac اذهب إلى File → Add to Dock... (macOS Sonoma 14+). في الإصدارات الأقدم لا يمكن التثبيت — استخدم Chrome/Edge.',
    faq_title: 'الأسئلة الشائعة',
    faq1_q: 'هل هو مجاني؟', faq1_a: 'نعم، تثبيت التطبيق مجاني 100%. الاشتراكات المميزة (Coach 9.99$، Player 14.99$، Academy 49.99$) اختيارية وتفتح ميزات متقدمة.',
    faq2_q: 'هل يعمل دون اتصال؟', faq2_a: 'نعم. بعد التثبيت، يخزن التطبيق الخطط المفتوحة ويمكنك تصفحها دون إنترنت. إنشاء/تعديل الخطط واستخدام مدرب الذكاء يحتاج اتصالاً.',
    faq3_q: 'هل يأخذ مساحة كبيرة؟', faq3_a: 'أقل من 10 ميجابايت. PlayMaster IA هو PWA — يستخدم ذاكرة المتصفح، لا يحتاج تنزيلاً كاملاً.',
    faq4_q: 'كيف أحذف التثبيت؟', faq4_a: 'iOS/iPadOS: اضغط مطولًا → "حذف". Android: اسحب إلى "إزالة". PC/Mac: افتح التطبيق → قائمة ⋮ → "إزالة".',
    faq5_q: 'لماذا لا يظهر زر التثبيت؟', faq5_a: 'ثلاث أسباب شائعة: (1) أنت في Firefox أو متصفح غير مدعوم — استخدم Chrome أو Edge. (2) التطبيق مثبت بالفعل. (3) المتصفح يحتاج تفاعلاً أكثر — تصفّح 30 ثانية ثم حدّث.',
    faq6_q: 'هل تعمل الإشعارات بعد التثبيت؟', faq6_a: 'نعم، على Android و PC/Mac. على iOS/iPadOS، تتطلب إشعارات الدفع لـ PWAs iOS 16.4+ ويجب تفعيلها يدويًا من الإعدادات → الإشعارات → PlayMaster IA.',
    back_home: 'العودة للرئيسية',
  },
};

function detect() {
  if (typeof window === 'undefined') return { platform: 'unknown', browser: 'unknown', isStandalone: false };
  const ua = window.navigator.userAgent || '';
  const isStandalone =
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator.standalone === true;
  let platform = 'desktop';
  const isIpadOS = /Mac/.test(ua) && (window.navigator.maxTouchPoints || 0) > 1;
  if (/iPad|iPhone|iPod/.test(ua) || isIpadOS) platform = 'ios';
  else if (/android/i.test(ua)) platform = 'android';
  let browser = 'other';
  if (/CriOS|Chrome|Chromium/.test(ua) && !/Edg/.test(ua)) browser = 'chrome';
  else if (/Edg/.test(ua)) browser = 'edge';
  else if (/FxiOS|Firefox/.test(ua)) browser = 'firefox';
  else if (/Safari/.test(ua) && !/CriOS|Chrome|Chromium/.test(ua)) browser = 'safari';
  return { platform, browser, isStandalone };
}

function Tab({ icon: Icon, label, active, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`flex-1 inline-flex flex-col items-center gap-1 py-3 px-2 rounded-md border-2 transition ${
        active ? 'bg-gold/15 border-gold text-gold' : 'border-steel text-gray-400 hover:border-gold/40'
      }`}
    >
      <Icon className="w-6 h-6" />
      <span className="text-xs font-h uppercase tracking-wider">{label}</span>
    </button>
  );
}

function Step({ n, icon: Icon, text }) {
  return (
    <div className="flex items-start gap-3">
      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gold text-navy font-h font-bold flex items-center justify-center">{n}</div>
      <div className="flex-1">
        <p className="text-sm text-white leading-relaxed inline-flex items-center gap-2">
          <Icon className="w-4 h-4 text-gold flex-shrink-0" /> {text}
        </p>
      </div>
    </div>
  );
}

function FAQ({ q, a }) {
  return (
    <details className="bg-navy-deep border border-steel rounded-lg p-3 group">
      <summary className="cursor-pointer text-sm text-white font-h uppercase tracking-wider flex items-center justify-between">
        {q}
        <ChevronRight className="w-4 h-4 text-gold transition group-open:rotate-90" />
      </summary>
      <p className="mt-2 text-xs text-gray-300 leading-relaxed">{a}</p>
    </details>
  );
}

export default function Install() {
  const { lang } = useLang();
  const s = I[lang] || I.es;
  const [info, setInfo] = useState({ platform: 'unknown', browser: 'unknown', isStandalone: false });
  const [deferred, setDeferred] = useState(null);
  const [status, setStatus] = useState(null);
  const [activeTab, setActiveTab] = useState('auto');

  useEffect(() => {
    const d = detect();
    setInfo(d);
    setActiveTab(d.platform === 'unknown' ? 'desktop' : d.platform);
    if (window.__pmDeferredInstall) setDeferred(window.__pmDeferredInstall);

    const onReady = () => setDeferred(window.__pmDeferredInstall);
    const onInstalled = () => { setStatus('installed'); setDeferred(null); };
    window.addEventListener('pm-install-ready', onReady);
    window.addEventListener('pm-app-installed', onInstalled);
    return () => {
      window.removeEventListener('pm-install-ready', onReady);
      window.removeEventListener('pm-app-installed', onInstalled);
    };
  }, []);

  const install = async () => {
    const ev = deferred || window.__pmDeferredInstall;
    if (!ev) { setStatus('error'); return; }
    setStatus('installing');
    try {
      await ev.prompt();
      const choice = await ev.userChoice;
      setStatus(choice.outcome === 'accepted' ? 'installed' : 'dismissed');
    } catch { setStatus('error'); }
    window.__pmDeferredInstall = null;
    setDeferred(null);
  };

  const { browser, isStandalone } = info;
  const canInstallNow = !!(deferred || window.__pmDeferredInstall);

  return (
    <div className="min-h-screen bg-navy-deep text-white px-4 py-10">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <Link to="/" data-testid="install-back-home" className="inline-block">
            <img src={LOGO} alt="PlayMaster IA" className="h-24 mx-auto mb-4" />
          </Link>
          <div className="text-xs font-mono uppercase tracking-[0.3em] text-gold mb-2">PlayMaster IA</div>
          <h1 className="font-h uppercase text-3xl sm:text-4xl mb-2">{s.title}</h1>
          <p className="text-gray-400 text-sm leading-relaxed max-w-md mx-auto">{s.subtitle}</p>
        </div>

        {isStandalone && (
          <div className="bg-emerald-900/30 border border-emerald-500/50 rounded-lg p-4 mb-6 text-center" data-testid="install-already">
            <Check className="w-6 h-6 text-emerald-400 inline-block mr-2" />
            <span className="text-emerald-300 font-h uppercase tracking-wider text-sm">{s.already_installed}</span>
          </div>
        )}

        {canInstallNow && activeTab !== 'ios' && (
          <div className="bg-card-steel border-2 border-gold rounded-lg p-5 mb-6 animate-glow-pulse" data-testid="install-direct-card">
            <div className="flex items-center gap-2 mb-2">
              <Zap className="w-4 h-4 text-gold" />
              <span className="text-xs font-mono uppercase tracking-widest text-gold">{s.quick_install_avail}</span>
            </div>
            <p className="text-sm text-gray-300 mb-3">{s.quick_install_supports}</p>
            <button
              onClick={install}
              data-testid="install-btn-direct"
              className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md bg-gold text-navy font-h uppercase tracking-wider font-semibold hover:bg-gold-light"
            >
              <Download className="w-4 h-4" /> {s.install_btn}
            </button>
            {status === 'installed' && <p className="text-emerald-400 text-xs mt-2 text-center">{s.installed_ok}</p>}
            {status === 'dismissed' && <p className="text-yellow-400 text-xs mt-2 text-center">{s.cancelled}</p>}
            {status === 'error' && <p className="text-red-400 text-xs mt-2 text-center">{s.dialog_error}</p>}
          </div>
        )}

        <div className="mb-3 text-xs text-gray-400 text-center">{s.choose_device}</div>
        <div className="flex gap-2 mb-6" data-testid="install-platform-tabs">
          <Tab icon={Apple} label={s.tab_ios} active={activeTab === 'ios'} onClick={() => setActiveTab('ios')} testid="tab-ios" />
          <Tab icon={Smartphone} label={s.tab_android} active={activeTab === 'android'} onClick={() => setActiveTab('android')} testid="tab-android" />
          <Tab icon={Monitor} label={s.tab_desktop} active={activeTab === 'desktop'} onClick={() => setActiveTab('desktop')} testid="tab-desktop" />
        </div>

        {activeTab === 'ios' && (
          <div className="bg-card-steel border border-gold/30 rounded-lg p-5 space-y-3 mb-4" data-testid="install-ios">
            <h2 className="font-h uppercase text-lg text-gold flex items-center gap-2">
              <Apple className="w-5 h-5" /> {s.ios_h}
            </h2>
            <div className="bg-blue-900/30 border border-blue-500/50 rounded p-3 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-blue-200">{s.ios_warn}</p>
            </div>
            <Step n="1" icon={Share2} text={s.ios_s1} />
            <Step n="2" icon={Plus} text={s.ios_s2} />
            <Step n="3" icon={Check} text={s.ios_s3} />
            <Step n="4" icon={Smartphone} text={s.ios_s4} />
          </div>
        )}

        {activeTab === 'android' && (
          <div className="bg-card-steel border border-gold/30 rounded-lg p-5 space-y-3 mb-4" data-testid="install-android">
            <h2 className="font-h uppercase text-lg text-gold flex items-center gap-2">
              <Smartphone className="w-5 h-5" /> {s.and_h}
            </h2>
            <p className="text-xs text-gray-400">{s.and_reco}</p>
            <Step n="1" icon={Smartphone} text={s.and_s1} />
            <Step n="2" icon={Plus} text={s.and_s2} />
            <Step n="3" icon={Share2} text={s.and_s3} />
            <Step n="4" icon={Check} text={s.and_s4} />
            <p className="text-[11px] text-gray-500 pt-2 border-t border-steel">{s.and_tip}</p>
            {/* Play Protect explainer — calms users who see the "blocked unsafe app" dialog */}
            <div className="mt-3 bg-yellow-900/20 border border-yellow-500/40 rounded p-3 flex items-start gap-2" data-testid="install-play-protect">
              <AlertTriangle className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs text-yellow-300 font-h uppercase tracking-wider mb-1">{s.and_play_protect_title}</p>
                <p className="text-xs text-yellow-200/90 leading-relaxed">{s.and_play_protect_body}</p>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'desktop' && (
          <div className="bg-card-steel border border-gold/30 rounded-lg p-5 space-y-3 mb-4" data-testid="install-desktop">
            <h2 className="font-h uppercase text-lg text-gold flex items-center gap-2">
              <Monitor className="w-5 h-5" /> {s.pc_h}
            </h2>
            <p className="text-xs text-gray-400">{s.pc_reco}</p>

            <div className="mt-3 mb-2">
              <div className="text-xs font-h uppercase tracking-wider text-gold mb-2">{s.pc_section}</div>
              <Step n="1" icon={Monitor} text={s.pc_s1} />
              <Step n="2" icon={Download} text={s.pc_s2} />
              <Step n="3" icon={Plus} text={s.pc_s3} />
              <Step n="4" icon={Check} text={s.pc_s4} />
            </div>

            {browser === 'firefox' && (
              <div className="bg-yellow-900/30 border border-yellow-500/50 rounded p-3 flex items-start gap-2 mt-3">
                <AlertTriangle className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-yellow-200">{s.firefox_warn}</p>
              </div>
            )}

            {browser === 'safari' && (
              <div className="bg-yellow-900/30 border border-yellow-500/50 rounded p-3 flex items-start gap-2 mt-3">
                <AlertTriangle className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-yellow-200">{s.safari_warn}</p>
              </div>
            )}
          </div>
        )}

        <div className="mt-8 mb-6">
          <h2 className="font-h uppercase text-lg text-gold mb-3">{s.faq_title}</h2>
          <div className="space-y-2">
            <FAQ q={s.faq1_q} a={s.faq1_a} />
            <FAQ q={s.faq2_q} a={s.faq2_a} />
            <FAQ q={s.faq3_q} a={s.faq3_a} />
            <FAQ q={s.faq4_q} a={s.faq4_a} />
            <FAQ q={s.faq5_q} a={s.faq5_a} />
            <FAQ q={s.faq6_q} a={s.faq6_a} />
          </div>
        </div>

        <div className="text-center pt-6 border-t border-gold/15">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-gold hover:underline" data-testid="install-back-link">
            <ChevronRight className="w-4 h-4 rotate-180" /> {s.back_home}
          </Link>
        </div>
      </div>
    </div>
  );
}
